import { getConfig } from '@/lib/get-config';
import type { BaseInstance, ModelName } from '@pigello/pigello-matrix';
import {
  useMutation,
  useQueryClient,
  type UseMutationOptions,
} from '@tanstack/react-query';
import { QUERY_KEYS } from '../constants';
import type { ErrorResponse, useDeleteGenericInstanceProps } from '../types';
import {
  invalidateConstructQueries,
  invalidateRelatedModelQueries,
} from './utils';

export const useDeleteGenericInstance = <Instance extends BaseInstance>(
  mutationFn: (
    options: useDeleteGenericInstanceProps<Instance>
  ) => Promise<Instance>,
  options: UseMutationOptions<Instance, ErrorResponse, unknown, unknown>,
  modelName: ModelName
) => {
  const queryClient = useQueryClient();
  return useMutation({
    ...options,
    mutationFn,
    onSuccess: async (data, variables, onMutateResult, context) => {
      const config = getConfig(modelName);
      try {
        await options.onSuccess?.(data, variables, onMutateResult, context);
      } finally {
        await Promise.all([
          queryClient.invalidateQueries({
            queryKey: [modelName, data.id],
          }),
          invalidateConstructQueries(queryClient, config?.contentType, data.id),
          queryClient.invalidateQueries({
            queryKey: [modelName, QUERY_KEYS.LIST],
          }),
          queryClient.invalidateQueries({
            queryKey: [modelName, QUERY_KEYS.ALL_INSTANCES],
          }),
          queryClient.invalidateQueries({
            queryKey: [modelName, QUERY_KEYS.ALL_IDS],
          }),
          queryClient.invalidateQueries({
            queryKey: [modelName, QUERY_KEYS.COUNT],
          }),
          invalidateRelatedModelQueries(queryClient, modelName),
        ]);
      }
    },
  });
};
